/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useContext } from "react";
import {
  AppBar,
  Avatar,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Toolbar,
  Typography,
  useScrollTrigger,
  IconButton,
  MenuItem,
  Menu,
  Box,
  Slide,
  Snackbar,
  Alert,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { ArrowBack } from "@mui/icons-material";
import { useAuth } from "../context/auth";
import MenuIcon from "@mui/icons-material/Menu";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import AccountBoxIcon from "@mui/icons-material/AccountBox";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import MapsHomeWorkIcon from "@mui/icons-material/MapsHomeWork";
import EnvironmentFlag from "./environmentFlag";
import { BrandingContext } from "@/branding/BrandingProvider";
import { NavigateTo, SetLocation } from "@/helper/navigation";
import type { TransitionProps } from "@mui/material/transitions";
import { usePwaInstall } from "@/usePwaInstall";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";

export const HeaderType = {
  Dashboard: 0,
  Profile: 1,
  Vehicle: 2,
  VehicleList: 3,
  WhatsMyCover: 4,
  AdditionalServices: 5,
  AccidentSupport: 6,
  AccidentSupportAdd: 7,
  AccidentSupportView: 8,
  AccidentSupportList: 9,
  AddressList: 10,
  Address: 11,
  AddEditAddress: 12,
  Scanner: 13,
} as const;
type HeaderType = (typeof HeaderType)[keyof typeof HeaderType];

interface Props {
  window?: () => Window;
  children: React.ReactElement;
}

interface HeaderProps {
  style?: any;
  type: HeaderType;
  onBack?: () => void;
}

function ElevationScroll(props: Props) {
  const { children, window } = props;
  const trigger = useScrollTrigger({
    disableHysteresis: true,
    threshold: 0,
    target: window ? window() : undefined,
  });

  return React.cloneElement(children as React.ReactElement<any>, {
    elevation: trigger ? 4 : 0,
  });
}

// iOS detection (no beforeinstallprompt there)
const isIOS = () =>
  typeof navigator !== "undefined" &&
  /iphone|ipad|ipod/i.test(navigator.userAgent);

// small dialog transition
const Transition = React.forwardRef(function Transition(
  props: TransitionProps & { children: React.ReactElement<any, any> },
  ref: React.Ref<unknown>
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const Header: React.FC<HeaderProps> = (props) => {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const menuOpen = Boolean(anchorEl);
  const { type } = props;
  const { logout } = useAuth();
  const [open, setOpen] = React.useState(false);
  const openConfirmationDialog = () => setOpen(true);
  const closeConfirmationDialog = () => setOpen(false);

  const navigate = useNavigate();
  const { tenant } = useParams<{ tenant: string }>();
  const branding = useContext(BrandingContext);
  const location = useLocation();

  // ---------------------------
  // Back button hiding rules
  // ---------------------------
  const FORM_ROUTES =
    /\/(dynamicform|dynamiclist|accidentSupportDraw|uploads|accidentSupportFinal)/i;

  const NO_BACK_TYPES: HeaderType[] = [
    HeaderType.AccidentSupportAdd,
    HeaderType.Scanner,
  ];

  const hideBackButton =
    NO_BACK_TYPES.includes(type) || FORM_ROUTES.test(location.pathname);

  // PWA install
  const { isInstallable, promptInstall } = usePwaInstall();
  const [installTipOpen, setInstallTipOpen] = React.useState(false);

  // Generic toast (also used to warn when offline)
  const [toast, setToast] = React.useState<null | {
    severity: "success" | "info" | "warning" | "error";
    msg: string;
  }>(null);

  // Online/offline state
  const [isOnline, setIsOnline] = React.useState<boolean>(
    typeof navigator !== "undefined" ? navigator.onLine : true
  );
  React.useEffect(() => {
    const on = () => setIsOnline(true);
    const off = () => setIsOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
    };
  }, []);

  const tpath = (p: string) => (tenant ? `/${tenant}/${p}` : `/${p}`);

  const routeToLogin = () => {
    logout();
    navigate(tpath("login"), { replace: true });
  };

  const navigateBack = () => {
    // 1) Simple known destinations
    if (
      type === HeaderType.Profile ||
      type === HeaderType.VehicleList ||
      type === HeaderType.AddressList ||
      type === HeaderType.AdditionalServices
    ) {
      navigate(tpath("dashboard"));
      return;
    }

    if (type === HeaderType.AccidentSupportList) {
      navigate(tpath("additionalservices"));
      return;
    }

    if (type === HeaderType.Vehicle) {
      localStorage.removeItem("vehicleFormData");
      localStorage.removeItem("linkedDocumentID");

      // Prefer explicit return target
      const storedFrom = localStorage.getItem("vehicleReturnTo");
      if (storedFrom === "accidentsupport") {
        localStorage.removeItem("AccidentForm");
        navigate(tpath("accidentsupport"), { replace: true });
        return;
      }
      if (storedFrom === "vehiclelist") {
        localStorage.removeItem("AccidentForm");
        navigate(tpath("vehiclelist"), { replace: true });
        return;
      }

      // Backward-compat with your old flag
      const accidentForm = localStorage.getItem("AccidentForm");
      if (accidentForm === "1") {
        localStorage.removeItem("AccidentForm");
        navigate(tpath("accidentsupport"), { replace: true });
        return;
      }

      // Default
      navigate(tpath("vehiclelist"), { replace: true });
      return;
    }

    // 3) “Form flow” pages → step back using LS index
    const isFormFlow = FORM_ROUTES.test(location.pathname);

    if (isFormFlow) {
      const params = new URLSearchParams(location.search);
      if (params.get("from") === "list") {
        navigate(-1); // straight back to list
        return;
      }
      const s = Number(params.get("step") ?? "0");

      if (s <= 0) {
        const incidentType = JSON.parse(
          localStorage.getItem("incidentType") || "null"
        );
        if (incidentType === "AccidentSupport") {
          navigate(tpath("accidentSupport"), { replace: true }); // single replace, no push
        } else {
          navigate(-1);
        }
        return;
      }

      SetLocation(s - 1);
      const clean = NavigateTo().replace(/^\/+/, "");
      navigate(tenant ? `/${tenant}/${clean}` : `/${clean}`, {
        replace: true,
      }); // back uses replace
      return;
    }

    // 2) **AccidentSupport hub** → back to the list
    if (type === HeaderType.AccidentSupport) {
      navigate(tpath("accidentSupportList"), { replace: true });
      return;
    }

    // 4) Fallback
    navigate(-1);
  };

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => setAnchorEl(null);

  // Guarded navigation that blocks when offline
  const guarded = (action: () => void, label: string) => {
    if (!isOnline) {
      setToast({
        severity: "warning",
        msg: `${label} is unavailable offline`,
      });
      return;
    }
    action();
  };

  const navigateProfile = () => {
    handleClose();
    navigate(tpath("profile"));
  };

  const navigateVehicleList = () => {
    handleClose();
    navigate(tpath("vehiclelist"));
  };

  const navigateAddressList = () => {
    handleClose();
    navigate(tpath("addresslist"));
  };

  const getHeaderTitle = (): string => {
    switch (type) {
      case HeaderType.Dashboard:
        return "Dashboard";
      case HeaderType.Profile:
        return "Profile";
      case HeaderType.Vehicle:
        return "Vehicle";
      case HeaderType.VehicleList:
        return "Vehicles";
      case HeaderType.AddEditAddress:
      case HeaderType.Address:
        return "Address";
      case HeaderType.AddressList:
        return "Addresses";
      case HeaderType.WhatsMyCover:
        return "Brochure";
      case HeaderType.AdditionalServices:
      case HeaderType.AccidentSupport:
        return "Forms";
      case HeaderType.AccidentSupportAdd:
        return "Form";
      case HeaderType.AccidentSupportList:
        return "Submitted Forms";
      case HeaderType.AccidentSupportView:
        return "Submitted Form";
      case HeaderType.Scanner:
        return "Scanner";
      default:
        return "";
    }
  };

  return (
    <>
      <React.Fragment>
        <ElevationScroll>
          <AppBar
            position="fixed"
            color="default"
            sx={{ bgcolor: "background.paper", height: 56 }}
          >
            <Toolbar
              sx={{
                minHeight: 56,
                display: "grid",
                gridTemplateColumns: "1fr auto 1fr",
                alignItems: "center",
                px: 1,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  justifySelf: "start",
                }}
              >
                {type === HeaderType.Dashboard || hideBackButton ? (
                  <Avatar alt="Logo" src={branding?.assets?.favicon} />
                ) : (
                  <IconButton
                    edge="start"
                    onClick={props.onBack ?? navigateBack}
                    color="inherit"
                    sx={{ p: 0.5 }}
                    aria-label="Go back"
                  >
                    <ArrowBack />
                  </IconButton>
                )}
                <EnvironmentFlag />
              </Box>

              <Typography
                variant="h6"
                color="primary"
                sx={{ textAlign: "center", justifySelf: "center" }}
              >
                {getHeaderTitle()}
              </Typography>

              <Box sx={{ justifySelf: "end" }}>
                <IconButton
                  aria-controls={menuOpen ? "header-menu" : undefined}
                  aria-haspopup="true"
                  aria-expanded={menuOpen ? "true" : undefined}
                  onClick={handleClick}
                  edge="end"
                  color="inherit"
                  aria-label="Open menu"
                >
                  <MenuIcon fontSize="large" />
                </IconButton>

                <Menu
                  id="header-menu"
                  anchorEl={anchorEl}
                  anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
                  transformOrigin={{ vertical: "top", horizontal: "center" }}
                  keepMounted
                  open={menuOpen}
                  onClose={handleClose}
                >
                  {/* Block these when offline */}
                  <MenuItem
                    onClick={() => guarded(navigateProfile, "Profile")}
                    disabled={!isOnline}
                    aria-disabled={!isOnline}
                  >
                    <AccountBoxIcon
                      fontSize="small"
                      style={{ marginRight: 8 }}
                    />
                    Profile
                  </MenuItem>

                  <MenuItem
                    onClick={() => guarded(navigateVehicleList, "Vehicles")}
                    disabled={!isOnline}
                    aria-disabled={!isOnline}
                  >
                    <DirectionsCarIcon
                      fontSize="small"
                      style={{ marginRight: 8 }}
                    />
                    Vehicles
                  </MenuItem>

                  <MenuItem
                    onClick={() => guarded(navigateAddressList, "Addresses")}
                    disabled={!isOnline}
                    aria-disabled={!isOnline}
                  >
                    <MapsHomeWorkIcon
                      fontSize="small"
                      style={{ marginRight: 8 }}
                    />
                    Addresses
                  </MenuItem>

                  {/* Install app (Android/desktop Chrome) */}
                  {isInstallable && (
                    <MenuItem
                      onClick={async () => {
                        handleClose();
                        try {
                          const res = await promptInstall(); // { outcome }
                          if (res?.outcome === "accepted") {
                            setToast({
                              severity: "success",
                              msg: "App installed 🎉",
                            });
                          } else if (res?.outcome === "dismissed") {
                            setToast({
                              severity: "info",
                              msg: "Install dismissed.",
                            });
                          } else {
                            setToast({
                              severity: "warning",
                              msg: "Install not available right now.",
                            });
                          }
                        } catch {
                          setToast({
                            severity: "warning",
                            msg: "Could not show install prompt.",
                          });
                        }
                      }}
                    >
                      <DownloadOutlinedIcon
                        fontSize="small"
                        style={{ marginRight: 8 }}
                      />
                      Install app
                    </MenuItem>
                  )}

                  {/* iOS tip when no beforeinstallprompt */}
                  {!isInstallable && isIOS() && (
                    <MenuItem
                      onClick={() => {
                        handleClose();
                        setInstallTipOpen(true);
                      }}
                    >
                      <DownloadOutlinedIcon
                        fontSize="small"
                        style={{ marginRight: 8 }}
                      />
                      Install app
                    </MenuItem>
                  )}

                  <MenuItem
                    onClick={() => {
                      handleClose();
                      openConfirmationDialog();
                    }}
                  >
                    <ExitToAppIcon
                      fontSize="small"
                      style={{ marginRight: 8 }}
                    />
                    Logout
                  </MenuItem>
                </Menu>
              </Box>
            </Toolbar>
          </AppBar>
        </ElevationScroll>
        <Toolbar />
      </React.Fragment>

      {/* iOS Add-to-Home-Screen tip */}
      <Dialog
        open={installTipOpen}
        TransitionComponent={Transition}
        keepMounted
        onClose={() => setInstallTipOpen(false)}
        aria-labelledby="install-tip-title"
        aria-describedby="install-tip-desc"
      >
        <DialogTitle id="install-tip-title">Install on iPhone</DialogTitle>
        <DialogContent>
          <DialogContentText id="install-tip-desc">
            In Safari, tap <strong>Share</strong> (square with an arrow) →{" "}
            <strong>Add to Home Screen</strong>, then tap <strong>Add</strong>.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setInstallTipOpen(false)}
            color="primary"
            autoFocus
          >
            Got it
          </Button>
        </DialogActions>
      </Dialog>

      {/* Logout confirmation */}
      <Dialog
        open={open}
        onClose={closeConfirmationDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">Please Confirm</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you would like to logout?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Grid container justifyContent="space-between" alignItems="center">
            <Grid>
              <Button onClick={closeConfirmationDialog} color="secondary">
                <strong>No</strong>
              </Button>
            </Grid>
            <Grid>
              <Button onClick={routeToLogin} color="primary" autoFocus>
                <strong>Yes</strong>
              </Button>
            </Grid>
          </Grid>
        </DialogActions>
      </Dialog>

      {/* Toasts (install/offline guard) */}
      {toast && (
        <Snackbar
          open={!!toast}
          autoHideDuration={3500}
          onClose={() => setToast(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert
            onClose={() => setToast(null)}
            severity={toast.severity}
            variant="filled"
            sx={{ width: "100%" }}
          >
            {toast.msg}
          </Alert>
        </Snackbar>
      )}
    </>
  );
};

export default Header;
